import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { initialNodes, initialEdges } from '../constants/flowConstants';

const EmailEditor = ({ content, recipientEmail, subject: propSubject, onChange, recruiterName: propRecruiterName }) => {
  const [recruiterName, setRecruiterName] = useState(propRecruiterName || '');
  const [emailSubject, setEmailSubject] = useState(propSubject || '');

  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState('gmail');

  const handleSendEmail = async () => {
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.email));
      const userData = userDoc.data();

      let endpoint;
      let emailData;
      let senderEmail;

      if (selectedService === 'gmail') {
        endpoint = `${process.env.REACT_APP_BACK_END_URL}/applybridge/send-email`;
        const token = userData.gmailToken;
        if (!token) {
          throw new Error('Gmail not connected. Please connect your account in settings.');
        }
        senderEmail = userData.connectedEmail; // Gmail connected email
        emailData = {
          access_token: token,
          user_email: senderEmail,
          to: recipientEmail,
          subject: emailSubject || 'Job Application',
          body: content
        };
      } else {
        endpoint = `${process.env.REACT_APP_BACK_END_URL}/applybridge/send-outlook-email`;
        emailData = {
          user_email: user.email,  // Keep this as the login email for now
          to: recipientEmail,
          subject: emailSubject || 'Job Application',
          body: content
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      // Initialize automation with the correct sender email
      await initializeAutomation(recipientEmail, senderEmail);

      setShowSuccess(true);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeAutomation = async (recipientEmail) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.email));
      const userData = userDoc.data();

      // Determine sender email based on selected service
      let senderEmail;
      if (selectedService === 'gmail') {
        senderEmail = userData.gmailEmail;
        if (!senderEmail || !userData.gmailToken) {
          throw new Error('Gmail not properly connected. Please reconnect your account.');
        }
      } else {
        senderEmail = userData.outlookEmail;
        if (!senderEmail || !userData.outlookAccessToken) {
          throw new Error('Outlook not properly connected. Please reconnect your account.');
        }
      }

      const companyMatch = content.match(/(?:at|joining|with)\s+([A-Z][A-Za-z\s]+)/);
      const companyName = companyMatch ? companyMatch[1].trim() : "your company";

      const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/generate-followup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName,
          recruiterName,
          originalEmail: content,
          applicantName: user.displayName || 'Your Name'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate follow-up email');
      }

      const { generatedFollowUp } = await response.json();

      const automationData = {
        userEmail: user.email,      // The logged-in user's email
        senderEmail: senderEmail,   // The actual email used for sending
        recipientEmail: recipientEmail,
        recruiterName,
        companyName: companyName,
        initialEmailSentDate: new Date().toISOString(),
        status: 'active',
        waitDays: 3,
        emailProvider: selectedService,
        followUpEmail: generatedFollowUp,
        originalEmail: content,
        subject: emailSubject,
        nodes: initialNodes,
        edges: initialEdges
      };

      const docRef = await addDoc(collection(db, 'emailAutomations'), automationData);

      setTimeout(() => {
        navigate(`/email-automation/${docRef.id}`);
      }, 2000);
      return docRef.id;
    } catch (error) {
      console.error('Error initializing automation:', error);
      throw error;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        label="Recruiter's Name"
        value={recruiterName}
        onChange={(e) => setRecruiterName(e.target.value)}
        sx={{ mb: 2 }}
        placeholder="Enter recruiter's name"
      />

      <TextField
        fullWidth
        label="Email Subject"
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        sx={{ mb: 2 }}
        placeholder="Enter email subject"
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Send from</InputLabel>
        <Select
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value)}
          label="Send from"
        >
          <MenuItem value="gmail">Gmail</MenuItem>
          <MenuItem value="outlook">Outlook</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        rows={12}
        value={content}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        sx={{
          mb: 2,
          '& .MuiInputBase-root': {
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap'
          }
        }}
      />

      <Button
        variant="contained"
        color="primary"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        onClick={handleSendEmail}
        disabled={loading || !recipientEmail}
        sx={{ mt: 2 }}
      >
        {loading ? 'Sending...' : `Send with ${selectedService.toUpperCase()}`}
      </Button>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" variant="filled">
          Email sent successfully! Automation flow has been initialized.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailEditor;